import React, { useMemo, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, { GeneralActions, Icons, RowActionsExtra } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { LoadingSpinner } from "../../../../../../../components/LoadingModal/styles";
import { useAppState } from "../../../../../../../context/StateContext";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../models/shipeezi.table";
import { Team } from "../../../../../../../models/team";
import TeamsService from "../../../../../../../services/teams.service";
import { Status } from "../../../../../../../util/enums";
import { TeamKeys } from "../../../../../../../util/queries";
import { getTeamTableColumns } from "./getTeamTableColumns";
import useStyles from "./styles";
import TeamModal, { HandleTeamModalProps } from "./TeamModal";

interface IUpdateTeam extends Team {
  isChangingStatus?: boolean;
}

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

export function TeamsComponent(props: Props) {
  const {permission} = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const teamModalRef = useRef<HandleTeamModalProps>(null);

  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { mutate, isLoading, variables } = useMutation({
    mutationKey: TeamKeys.save(),
    mutationFn: async (team: IUpdateTeam) => {
      if (team && team.id)
        await TeamsService.update(team.id, { ...team, companyId: team.companyId! });
      else await TeamsService.create({ ...team, companyId: currentUser?.companyId! });
    },
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));

      teamModalRef.current?.handleCloseModal();
      refetchTeamList();
      refetchCountTeam();
    },
  });

  const branchesIds = useMemo(() => {
    if (!currentUser || !currentUser?.branches || !currentUser?.companyId) {
      return [];
    }

    return currentUser.branches.filter(branch => branch.companyId === currentUser.companyId)?.map(branch => branch.id!)
  }, [currentUser?.branches])

  const {
    data: teamsList,
    isFetching,
    refetch: refetchTeamList,
  } = useQuery({
    queryKey: TeamKeys.getAll([page, pageSize, searches, sorting, filters, currentUser?.branches]),
    queryFn: async () => {
      const response = await TeamsService.getAll({
        page,
        pageSize,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
        sortBy: sorting?.length ? JSON.stringify(sorting) : "",
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            association: "memberLeader",
          },
          {
            association: "members",
          },
          {
            association: "fleets",
          },
          {
            association: "branches",
            where: { id: currentUser?.branches?.map((branch) => branch.id), companyId: currentUser?.companyId },
            required: false,
          }
        ]),
        ...filters,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const {
    data: teamsTotal,
    isFetching: isCounting,
    refetch: refetchCountTeam,
  } = useQuery({
    queryKey: TeamKeys.count([page, pageSize, searches, sorting, filters, currentUser?.branches]),
    queryFn: async () => {
      const response = await TeamsService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        branchesIds,
        include: JSON.stringify([
          {
            association: "branches",
            where: { id: currentUser?.branches?.map((branch) => branch.id), companyId: currentUser?.companyId },
            required: false,
          }
        ]),
        ...filters,
      });

      if (response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));

    setSorting(sorting);
  };

  const renderChangeStatusButton = (index: number, team: Team) => {
    if (
      isLoading &&
      variables?.id === team.id &&
      index !== undefined &&
      variables?.isChangingStatus
    ) {
      return <LoadingSpinner size={20} />;
    }

    return (
      <Tooltip title={translate("changeStatus")} placement="top">
        <div>
          <Icons.status
            className={classes.iconExtraAction}
            onClick={() => {
              if (team) {
                mutate({
                  ...team,
                  isActive: !team.isActive,
                  isChangingStatus: true,
                });
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const renderEditButton = (_index: number, team: Team) => {
    return (
      <Tooltip title={translate("edit")} placement="top">
        <div>
          <Icons.edit
            className={classes.iconExtraAction}
            size={18}
            color="#475467"
            onClick={() => teamModalRef.current?.handleOpenModal(team)}
          />
        </div>
      </Tooltip>
    );
  };

  const columns = getTeamTableColumns({ translate });

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleNewRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "isActive",
          options: [
            { label: Status.ACTIVE, value: "1" },
            { label: Status.INACTIVE, value: "0" },
          ],
        },
      ],
      onChange: handleFilter,
    },
    handleNewRecord: (permission && !permission.create)
    ? undefined
    : {
      text: translate("newTeam"),
      action: () => teamModalRef.current?.handleOpenModal({} as Team),
      tooltip: translate("newTeam"),
    },
  };

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [];

    if (permission && permission.edit) {
      actions.push({
        title: translate("edit"),
        key: "edit",
        buttonAndAction: renderEditButton,
      });
    }

    actions?.push({
      title: translate("changeStatus"),
      key: "changeStatus",
      buttonAndAction: renderChangeStatusButton,
    })

    return actions;
  }

  const rowActionsExtra: RowActionsExtra[] = handleRowActionsExtraByPermission();

  return (
    <div>
      <ShipeeziNewGrid
        data={teamsList || []}
        columns={columns}
        totalItems={Number(teamsTotal || 0)}
        loadingData={isFetching || isCounting}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["edit", "changeStatus"],
        }}
        rowActionsExtra={rowActionsExtra}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />

      <TeamModal ref={teamModalRef} editTeam={mutate} createTeam={mutate} isLoading={isLoading} />
    </div>
  );
}
